(() => {
  const ROOT_SELECTOR = "[data-product-social-gallery]";
  const PENDING = "data-social-gallery-pending";
  const LAYOUT_STYLE_ID = "psg-videowise-layout-style";
  const LINKED_PRODUCTS_STYLE_ID = "psg-linked-products-style";
  const PLAY_BUTTON_CLASS = "ugc-vw-play";
  const FAST_WAIT_MS = 250;
  const SLOW_WAIT_MS = 1000;
  const FAST_WAIT_ATTEMPTS = 80;
  /* Hide the gallery chrome if Videowise never mounts real media. */
  const MAX_WAIT_MS = 4000;
  const SLIDE_GAP = 16;
  /*
   * Videowise no longer wraps each thumb in a `role="button"` element — the
   * media wrapper is now a plain div labelled by the video title. Match both
   * shapes, then the slide's first child, so the tile rules and the play mark
   * survive the next rename.
   */
  const TILE_PARTS = [
    '[role="group"] > [role="button"]',
    '[role="group"] > [aria-labelledby]',
    '[role="group"] > :first-child',
  ];
  const TILE_SELECTOR = TILE_PARTS.join(", ");
  const TILE_CSS_SELECTOR = TILE_PARTS.map((part) => `#vw-root ${part}`).join(
    ",\n    ",
  );
  /* Three portrait thumbs fill the buy-column width. */
  const VISIBLE_SLIDES = 3;
  const MIN_THUMB_WIDTH = 96;
  /* Play mark — 24×24, fill white @ 0.8 */
  const PLAY_ICON_SVG = `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 0C5.38105 0 0 5.38105 0 12C0 18.6189 5.38105 24 12 24C18.6189 24 24 18.6189 24 12C24 5.38105 18.6189 0 12 0ZM15.8653 12.6063L10.2063 16.3705C9.72632 16.6989 9.06947 16.3453 9.06947 15.7642V8.23579C9.06947 7.65474 9.72632 7.30105 10.2063 7.62947L15.8653 11.3937C16.2947 11.6716 16.2947 12.3284 15.8653 12.6063Z" fill="white" fill-opacity="0.8"/>
    </svg>
  `.trim();

  const HIDE_LINKED_PRODUCTS_CSS = `
    .vw--inline-shoppable-product {
      display: none !important;
    }
  `;

  const layoutCss = (thumbWidth) => `
    /* PDP — compact column carousel, no chrome */
    #vw-root .vw-previous-slide-button,
    #vw-root .vw-next-slide-button,
    #vw-root .vw-carousel-skipped,
    #vw-root [class*="navigation-dot"],
    #vw-root [class*="NavigationDot"] {
      display: none !important;
      pointer-events: none !important;
    }

    /* Drop Videowise outer margins so thumbs can span the column */
    #vw-root [class^="rw__inline-widget--container-"] > div:first-child {
      margin-inline: 0 !important;
      padding-inline: 0 !important;
      width: 100% !important;
    }

    #vw-root [role="group"] {
      flex: 0 0 ${thumbWidth}px !important;
      margin: 0 !important;
      margin-inline: 0 !important;
      margin-inline-end: 0 !important;
      margin-right: 0 !important;
      margin-top: 0 !important;
      max-width: ${thumbWidth}px !important;
      width: ${thumbWidth}px !important;
    }

    /*
     * Videowise reserves the carousel viewport and every slide in pixels,
     * measured from its own thumb size. Ours are narrower, so those heights
     * leave a tall empty band under the row — let content size them instead.
     */
    #vw-root div:has(> .vw-previous-slide-button),
    #vw-root [role="group"] {
      height: auto !important;
    }

    ${TILE_CSS_SELECTOR} {
      aspect-ratio: 262 / 387;
      border-radius: var(--radius--sm, 4px) !important;
      flex: 0 0 auto !important;
      height: auto !important;
      overflow: hidden !important;
      position: relative !important;
      width: 100% !important;
    }

    #vw-root .ugc-vw-play {
      backdrop-filter: blur(2.4px);
      -webkit-backdrop-filter: blur(2.4px);
      height: 24px;
      left: 50%;
      pointer-events: none;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 24px;
      z-index: 2;
    }

    #vw-root .ugc-vw-play[hidden] {
      display: none;
    }

    #vw-root .ugc-vw-play svg {
      display: block;
      height: 24px;
      width: 24px;
    }
  `;

  const hasLightDomMedia = (root) => {
    if (!root) return false;

    const media = root.querySelectorAll("img, video, iframe");

    for (const node of media) {
      if (node.offsetParent === null && node.getClientRects().length === 0) {
        continue;
      }

      if (node.tagName === "IMG") {
        if (node.naturalWidth > 0) return true;
        if (node.complete && node.currentSrc) return true;
        continue;
      }

      if (node.tagName === "VIDEO") {
        if (node.videoWidth > 0) return true;
        if (node.querySelector("source[src]")) return true;
        continue;
      }

      if (node.tagName === "IFRAME" && node.getAttribute("src")) {
        return true;
      }
    }

    return false;
  };

  const resolveVideowiseRoot = (mount) => {
    const widget = mount?.querySelector(".reeview-app-widget");
    const roots = window.videowiseShadowRoots;

    if (!widget || !roots || typeof roots !== "object") return null;

    const rawId = widget.id || "";
    const shortId = rawId.replace(/^reeview-app-widget_/, "");
    const candidateKeys = [
      rawId,
      shortId,
      shortId ? `reeview-app-widget_${shortId}` : "",
    ].filter(Boolean);

    for (const key of candidateKeys) {
      if (roots[key] instanceof ShadowRoot) return roots[key];
    }

    for (const value of Object.values(roots)) {
      if (!(value instanceof ShadowRoot)) continue;

      const host = value.host;

      if (!(host instanceof Element)) continue;

      if (host === widget || widget.contains(host) || mount.contains(host)) {
        return value;
      }
    }

    return null;
  };

  const hasVideowiseMedia = (mount) => {
    const shadow = resolveVideowiseRoot(mount);

    if (!shadow) return false;

    // Empty `#vw-root` shells do not count — require slides or real media.
    if (shadow.querySelectorAll('[role="group"]').length > 0) {
      return true;
    }

    return hasLightDomMedia(shadow);
  };

  const hasVisibleMedia = (mount) =>
    hasLightDomMedia(mount) || hasVideowiseMedia(mount);

  const trackWidthFor = (gallery, widget, shadow) => {
    const embed = gallery?.querySelector("[data-videowise-mount]");
    const track =
      shadow?.querySelector("#slide-0")?.parentElement ||
      shadow?.querySelector('[role="group"]')?.parentElement;

    return (
      track?.clientWidth ||
      embed?.clientWidth ||
      widget?.clientWidth ||
      gallery?.clientWidth ||
      0
    );
  };

  const thumbWidthFor = (gallery, widget, shadow) => {
    const available = trackWidthFor(gallery, widget, shadow);

    if (available <= 0) return MIN_THUMB_WIDTH;

    const gaps = SLIDE_GAP * (VISIBLE_SLIDES - 1);

    return Math.max(
      MIN_THUMB_WIDTH,
      Math.floor((available - gaps) / VISIBLE_SLIDES),
    );
  };

  const upsertStyle = (shadow, id, css) => {
    let style = shadow.getElementById(id);

    if (!style) {
      style = document.createElement("style");
      style.id = id;
      shadow.append(style);
    }

    style.textContent = css;
  };

  const applyLinkedProductsPreference = (gallery, shadow) => {
    const show = gallery.getAttribute("data-show-linked-products") === "true";

    if (show) {
      shadow.getElementById(LINKED_PRODUCTS_STYLE_ID)?.remove();
      return;
    }

    upsertStyle(shadow, LINKED_PRODUCTS_STYLE_ID, HIDE_LINKED_PRODUCTS_CSS);
  };

  const applyTrackGap = (shadow) => {
    const track =
      shadow.querySelector("#slide-0")?.parentElement ||
      shadow.querySelector('[role="group"]')?.parentElement;

    if (!track) return;

    track.style.display = "flex";
    track.style.gap = `${SLIDE_GAP}px`;
    track.style.columnGap = `${SLIDE_GAP}px`;
  };

  /* Belt and braces for the height reset above: walk the track's ancestors and
     drop any pixel height Videowise wrote inline, in case the prev/next button
     the CSS keys off ever stops rendering. */
  const clearReservedHeights = (shadow) => {
    const track =
      shadow.querySelector("#slide-0")?.parentElement ||
      shadow.querySelector('[role="group"]')?.parentElement;

    for (let el = track; el && el.id !== "vw-root"; el = el.parentElement) {
      if (el.style.height) el.style.height = "auto";
    }

    for (const slide of shadow.querySelectorAll('[role="group"]')) {
      if (slide.style.height) slide.style.height = "auto";
    }
  };

  const applyLayout = (gallery, shadow, widget) => {
    const width = thumbWidthFor(gallery, widget, shadow);

    upsertStyle(shadow, LAYOUT_STYLE_ID, layoutCss(width));
    applyTrackGap(shadow);
    clearReservedHeights(shadow);

    for (const slide of shadow.querySelectorAll('[role="group"]')) {
      slide.style.flex = `0 0 ${width}px`;
      slide.style.width = `${width}px`;
      slide.style.maxWidth = `${width}px`;
      /* Videowise often spaces via margin — clear it so gap alone controls rhythm */
      slide.style.margin = "0";
      slide.style.marginInline = "0";
      slide.style.marginInlineEnd = "0";
      slide.style.marginRight = "0";
    }
  };

  const mountPlayOverlays = (shadow, signal) => {
    for (const button of shadow.querySelectorAll(TILE_SELECTOR)) {
      if (button.querySelector(`.${PLAY_BUTTON_CLASS}`)) continue;

      const video = button.querySelector("video");

      if (!video) continue;

      const play = document.createElement("span");

      play.className = PLAY_BUTTON_CLASS;
      play.setAttribute("aria-hidden", "true");
      play.innerHTML = PLAY_ICON_SVG;
      button.append(play);

      const isPlayable = () => {
        const src = video.currentSrc || video.getAttribute("src") || "";

        return Boolean(src) && !src.endsWith("/") && !video.error;
      };

      const syncVisibility = () => {
        play.hidden = !isPlayable() || !video.paused;
      };

      video.addEventListener("play", syncVisibility, { signal });
      video.addEventListener("playing", syncVisibility, { signal });
      video.addEventListener("pause", syncVisibility, { signal });
      video.addEventListener("ended", syncVisibility, { signal });
      video.addEventListener("error", syncVisibility, { signal });
      video.addEventListener("loadedmetadata", syncVisibility, { signal });
      video.addEventListener("emptied", syncVisibility, { signal });
      syncVisibility();
    }
  };

  const enhanceShadow = (gallery, shadow, widget, signal) => {
    applyLinkedProductsPreference(gallery, shadow);
    applyLayout(gallery, shadow, widget);
    mountPlayOverlays(shadow, signal);
  };

  const revealOrHide = (gallery) => {
    if (!gallery?.hasAttribute(PENDING)) return false;

    const mount = gallery.querySelector("[data-videowise-mount]");

    gallery.removeAttribute(PENDING);
    const visible = hasVisibleMedia(mount);

    if (!visible) {
      gallery.setAttribute(PENDING, "");
      return false;
    }

    return true;
  };

  const watchGallery = (gallery) => {
    if (
      !gallery?.hasAttribute(PENDING) &&
      gallery.dataset.psgReady === "true"
    ) {
      return;
    }

    if (gallery.dataset.socialGalleryWatching === "true") return;

    gallery.dataset.socialGalleryWatching = "true";

    const mount = gallery.querySelector("[data-videowise-mount]");
    const widget = mount?.querySelector(".reeview-app-widget");

    if (!mount) {
      gallery.remove();
      return;
    }

    const abort = new AbortController();
    const { signal } = abort;
    let attempts = 0;
    let timer = 0;
    let playObserver = null;
    const startedAt = performance.now();

    const cleanupTimers = () => {
      window.clearTimeout(timer);
    };

    const giveUp = () => {
      cleanupTimers();
      observer.disconnect();
      playObserver?.disconnect();
      abort.abort();
      gallery.dataset.socialGalleryWatching = "false";

      if (gallery.hasAttribute(PENDING) && !hasVisibleMedia(mount)) {
        gallery.remove();
      }
    };

    const onReady = (shadow) => {
      enhanceShadow(gallery, shadow, widget, signal);
      gallery.dataset.psgReady = "true";

      playObserver?.disconnect();

      const host = shadow.querySelector("#vw-root") || shadow;

      playObserver = new MutationObserver(() => {
        enhanceShadow(gallery, shadow, widget, signal);
      });

      playObserver.observe(host, { childList: true, subtree: true });

      const onResize = () => applyLayout(gallery, shadow, widget);

      window.addEventListener("resize", onResize, { signal });
    };

    const observer = new MutationObserver(() => {
      tick();
    });

    observer.observe(mount, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    const tick = () => {
      if (!gallery.isConnected) {
        cleanupTimers();
        observer.disconnect();
        playObserver?.disconnect();
        abort.abort();
        return;
      }

      if (performance.now() - startedAt >= MAX_WAIT_MS) {
        giveUp();
        return;
      }

      const shadow = resolveVideowiseRoot(mount);
      const revealed = !gallery.hasAttribute(PENDING) || revealOrHide(gallery);

      if (shadow && (revealed || hasVideowiseMedia(mount))) {
        if (gallery.hasAttribute(PENDING)) {
          gallery.removeAttribute(PENDING);
        }

        cleanupTimers();
        observer.disconnect();
        onReady(shadow);
        return;
      }

      attempts += 1;
      const delay = attempts > FAST_WAIT_ATTEMPTS ? SLOW_WAIT_MS : FAST_WAIT_MS;

      timer = window.setTimeout(tick, delay);
    };

    tick();
  };

  const init = () => {
    for (const gallery of document.querySelectorAll(ROOT_SELECTOR)) {
      if (
        gallery.hasAttribute(PENDING) ||
        gallery.dataset.psgReady !== "true"
      ) {
        gallery.dataset.socialGalleryWatching = "";
        watchGallery(gallery);
      }
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  document.addEventListener("colourway:change", init);
})();
